import {
  derived,
  get,
  writable,
  type Readable,
  type Unsubscriber,
  type Writable,
} from "svelte/store";
import {
  StatusD10Enum,
  type Author,
  type GenericFolderItem,
  CategoryTypeEnum,
} from "@outverse-video/outverse-api-client-lib";
import { parseDate } from "app/utils/datetimes";
import { alerts } from "./alert";
import type { FolderTransportLayer, JSONDocument, JSONFolder } from "./documents_api";
import {
  DocumentTypeEnum,
  type Document,
  type Folder,
  type WritableDocument,
  type WritableFolder,
} from "app/models/documents";
import * as _ from "lodash-es";
import axios, { CanceledError } from "axios";
import * as Sentry from "@sentry/sveltekit";
import { browser } from "$app/environment";
import { reportError } from "app/utils/errors";
import { withResolvers } from "app/utils";

/**
 * Helper function to get dirty fields in an object
 * @param previous the previous object
 * @param current the current object
 * @param initialVal the initial value to start with
 * @param list of field names to ignore differences
 * returns:
 *  dirty - the dirty fields
 *  isDirty - true if there are dirty fields
 */
function getDirtyFields<Type extends Record<string, any>, Initial extends Record<keyof Type, any>>(
  previous: Type,
  current: Type,
  initialVal: Initial,
  ignore: Array<string> = []
): { fields: Partial<Type> & Initial; isDirty: boolean } {
  const fields: Partial<Type> & Initial = { ...initialVal };
  let isDirty = false;
  Object.entries(current).forEach(([key, value]) => {
    if (ignore.includes(key)) return;
    // save dirty fields and set flag
    if (!_.isEqual(value, previous[key])) {
      // @ts-ignore
      fields[key] = value;
      isDirty = true;
    }
  });
  return { fields, isDirty };
}

const registerFolderItems = (
  parentUuid: string,
  docStore: DocumentsStore,
  items: Array<GenericFolderItem>,
  depth: number
) => {
  return items.map((item) => {
    if (item.folderItemType === "nested_document") {
      return docStore.updateDocs([item])[0];
    } else {
      return docStore.update(
        [
          {
            ...item.folder,
            parentUuid,
            myPermissions: item.myPermissions,
            folderItemType: item.folderItemType,
          },
        ],
        depth + 1
      )[0];
    }
  });
};

export function onlyDocs(items: Array<WritableDocument | WritableFolder>) {
  return items.filter((item) => item.folderItemType === "nested_document") as WritableDocument[];
}

export function onlyFolders(items: Array<WritableDocument | WritableFolder>) {
  return items.filter((item) => item.folderItemType === "nested_folder") as WritableFolder[];
}

const flattenSections = (folders: WritableFolder[]): WritableDocument[] => {
  // Only sections and top level categories have no parent
  const sections = folders.filter((f) => !get(f).parentUuid);
  let documents: WritableDocument[] = [];

  sections.forEach((section) => {
    const sectionChildren = get(section).items;
    sectionChildren.forEach((item) => {
      if (item.folderItemType === "nested_document") {
        documents.push(item as WritableDocument);
      } else {
        const folder = item as WritableFolder;
        documents = documents.concat(get(folder).items as WritableDocument[]);
      }
    });
  });
  return documents;
};

/** An auto-saving writable store for a Folder with methods for adding/removing documents */
export const FolderStore = (
  docStore: DocumentsStore,
  json: JSONFolder,
  depth: number
): WritableFolder => {
  const folder: Folder = {
    ...json,
    items: registerFolderItems(json.uuid, docStore, json.items, depth),
  };

  const { set, update, subscribe } = writable<Folder>(folder);
  const docs = derived({ subscribe }, ($folder) => onlyDocs($folder.items));
  const folders = derived({ subscribe }, ($folder) => onlyFolders($folder.items));

  let autoSave = true;
  let inited = false;

  let oldFolder = { ...folder };

  // auto-save on changes
  subscribe(async (updatedFolder) => {
    // don't save the initial object
    if (inited && autoSave) {
      // patch only dirty fields
      const { fields, isDirty } = getDirtyFields(
        oldFolder,
        updatedFolder,
        // type issue: not sure how to make the final object have this as required
        { uuid: updatedFolder.uuid } as any,
        [
          "docs",
          "folders",
          "items",
          "documents",
          "myPermissions",
          "folderItemType",
          "parentUuid",
          "items",
        ] // readonly attributes
      );

      if (isDirty) {
        try {
          docStore.preventUpdates();
          await docStore.api.updateFolder(fields, docStore.communitySlug);
          oldFolder = { ...updatedFolder };
        } catch (e) {
          if (axios.isAxiosError(e)) {
            if (e.response) {
              alerts.error("Failed to save, please refresh and try again");
              // something weird has happened, as we're quite flexible so report it
              Sentry.captureException(e);
            } else {
              // network error, nothing we can do
              alerts.error("Failed to save, please check your connection and try again.");
            }
          }
        }
      }
    }
    inited = true;
  });

  return {
    uuid: folder.uuid, // avoid use of get when iterating/searching through list
    folderItemType: json.folderItemType,
    depth,
    docs,
    folders,
    set,
    subscribe,
    update,
    getAllSubFolders() {
      const allSubFolders: WritableFolder[] = [];
      const immediateChildren = onlyFolders(get(this).items);

      immediateChildren.forEach((f) => {
        allSubFolders.push(f);
        allSubFolders.push(...f.getAllSubFolders());
      });
      return allSubFolders;
    },
    updateFromJson: (json: JSONFolder, depth: number) => {
      autoSave = false;
      update((state) => {
        state = {
          ...state,
          ...json,
          items: registerFolderItems(json.uuid, docStore, json.items, depth),
        };
        return state;
      });
      autoSave = true;
    },
    addItem: (item: WritableDocument | WritableFolder) => {
      update((folder) => {
        // only add document if not already present.
        // If we have just moved the document to a specific index, then special logic is applied in document.move.
        // This function just handles the more simple case where a document's folder is changed without being moved
        // to a specific index.
        if (!folder.items.find((el) => el.uuid === item.uuid)) {
          folder.items.push(item);
        }
        return folder;
      });
    },
    removeItem: (uuid: string) => {
      update((folder) => {
        return {
          ...folder,
          items: folder.items.filter((item) => item.uuid !== uuid),
        };
      });
    },
    moveAfterDragAndDrop(index: number, parent?: Folder) {
      docStore.preventUpdates();
      const storeFolders = get(docStore.folders);

      if (!parent) {
        get(this).parentUuid = undefined;
        this.depth = 0;
        docStore.updateFolder(this);
        return docStore.api.moveFolder(docStore.communitySlug, folder.uuid, index);
      }

      const newParent = storeFolders.find((f) => f.uuid === parent.uuid)!;
      this.update((f) => ({ ...f, parentUuid: newParent.uuid }));
      this.depth = newParent.depth + 1;
      docStore.updateFolder(this);
      // trigger children to update to show new grandparent
      get(this.docs).forEach((d) => d.update((doc) => doc));
      return docStore.api.moveDocument(docStore.communitySlug, folder.uuid, parent.uuid, index);
    },
    async move(parent: Folder) {
      docStore.preventUpdates();

      const index = parent.items.length;

      const storeFolders = get(docStore.folders);
      const previousParentUuid = get(this).parentUuid;

      // Remove from previous location
      if (previousParentUuid) {
        const previousParent = storeFolders.find((f) => f.uuid === previousParentUuid);
        previousParent?.removeItem(this.uuid);
      } else {
        docStore.topLevelFolders.update((topLevelFolders) => [
          ...topLevelFolders.filter((f) => f.uuid !== this.uuid),
        ]);
      }

      const newParent = storeFolders.find((f) => f.uuid === parent.uuid)!;
      newParent.addItem(this);
      this.update((f) => ({ ...f, parentUuid: newParent.uuid }));
      this.depth = newParent.depth + 1;
      docStore.updateFolder(this);
      // trigger children to update to show new grandparent
      get(this.docs).forEach((d) => d.update((doc) => doc));
      return docStore.api.moveDocument(docStore.communitySlug, folder.uuid, parent.uuid, index);
    },
    delete: () => {
      docStore.deleteFolder(folder);
      docStore.api.deleteFolder(folder.uuid, docStore.communitySlug);
    },
  };
};

export const DocumentStore = (docStore: DocumentsStore, doc: JSONDocument): WritableDocument => {
  // deserialise to internal model
  const _doc: Document = {
    ...doc,
    status: doc.status || StatusD10Enum.Draft,
    modified: parseDate(doc.modified),
    created: parseDate(doc.created) || new Date(),
    publishedAt: parseDate(doc.publishedAt),
    draftUpdatedAt: parseDate(doc.draftUpdatedAt) || new Date(),
    latestVersionAt: parseDate(doc.latestVersionAt) || new Date(),
  };
  const { set, update, subscribe } = writable<Document>(_doc);
  const parentSection = derived({ subscribe }, ($doc) => {
    const parentFolderList = $doc.folder;
    if (!parentFolderList || $doc.docType === DocumentTypeEnum.Snippet) {
      return;
    }
    const parent = get(docStore.getFolder(parentFolderList.uuid));
    if (parent.categoryType === CategoryTypeEnum.Section) {
      return parent;
    }
    const grandParentUuid = parent.parentUuid;
    if (grandParentUuid) {
      return get(docStore.getFolder(grandParentUuid));
    }
  });

  let debounceTimeout: NodeJS.Timeout | undefined;
  let autoSave = true;
  let inited = false;
  // promise that resolves after a doc is saved
  let saving = withResolvers();
  // keep track of previous version
  let oldDoc: Document = { ..._doc };

  /** Wait for the document api save to complete after any changes made in fn.
   *
   * Awaits any ongoing saves to prevent clobbering.
   */
  async function waitForSave(fn: CallableFunction) {
    await saving.promise;
    saving = withResolvers();
    fn();
    return await saving.promise;
  }
  function updateFromJson(json: Partial<JSONDocument>) {
    const doc = get({ subscribe });
    // abort if nothing changed
    const modified = parseDate(json.modified);
    if (doc.content && doc.modified && modified && doc.modified >= modified) {
      return;
    }
    autoSave = false;
    update((doc) => ({
      ...doc,
      ...json,
      created: parseDate(json.created)!,
      publishedAt: parseDate(doc.publishedAt as any),
      modified,
      draftUpdatedAt: parseDate(json.draftUpdatedAt)!,
      latestVersionAt: parseDate(json.latestVersionAt)!,
    }));
    oldDoc = { ...get({ subscribe }) };
    autoSave = true;
  }

  // auto-save on changes
  subscribe((_doc) => {
    // don't save the initial object
    if (!inited) {
      inited = true;
      saving.resolve();
      return;
    }

    if (!autoSave) {
      saving.resolve();
      return;
    }

    // patch only dirty fields
    const changeset = getDirtyFields(oldDoc, _doc, { uuid: _doc.uuid } as any, [
      "folder",
      "draftUpdatedAt", // ignore because this is an auto-field
    ]);

    if (!changeset.isDirty) {
      saving.resolve();
      return;
    }

    docStore.preventUpdates();
    if (debounceTimeout) clearTimeout(debounceTimeout);

    // debounce updates
    debounceTimeout = setTimeout(async () => {
      const prevDoc = { ...oldDoc };
      try {
        oldDoc = { ..._doc };
        // assume our changes were successful to not clobber ongoing changes
        await docStore.api.updateDocument(changeset.fields, docStore.communitySlug);
        saving.resolve();
      } catch (e) {
        console.log(e);
        oldDoc = { ...prevDoc };

        if (axios.isAxiosError(e)) {
          // cancelled request already handled by request interceptor when maintenance mode is on
          if (e.code === "ERR_CANCELED") {
            alerts.error("Request could not be completed.");
            return;
          }

          if (e.response) {
            alerts.error("Failed to save, please check your document settings and try again");
            // something weird has happened, as we're quite flexible so report it
            Sentry.captureException(e);
          } else {
            // network error, nothing we can do
            alerts.error("Failed to save, please check your connection and try again.");
          }
        }
        saving.reject(e);
      }
    }, 500);
  });

  // force docStore readable documents to update
  docStore.documents.update((v) => v);

  return {
    uuid: doc.uuid,
    folderItemType: "nested_document",
    parentSection,
    set,
    subscribe,
    update,
    waitForSave,
    saveDraft: async function (content: Pick<Document, "content">, draftJson: Record<string, any>) {
      const doc = get(this);
      return await docStore.api.saveDraftDocument(
        { uuid: doc.uuid, ...content },
        docStore.communitySlug,
        draftJson
      );
    },
    toggleStatus: function () {
      const doc = get(this);
      const status =
        doc.status === StatusD10Enum.Draft ? StatusD10Enum.Published : StatusD10Enum.Draft;
      this.setStatus(status);
      return status;
    },
    setStatus: (status: StatusD10Enum) => {
      update((doc) => {
        doc.status = status;
        doc.isDraft = StatusD10Enum.Draft === status;
        doc.isPublished = StatusD10Enum.Published === status;
        return doc;
      });
    },
    setAuthors: (authors: Author[]) => {
      update((doc) => {
        doc.authors = authors;
        return doc;
      });
    },
    async toggleFavourite() {
      const favourite = get(this).favourite;
      // if we only had the List serializer version of the doc, we won't have
      // the favourite field, so we can't know its value
      if (favourite !== undefined) {
        update((doc) => ({ ...doc, favourite: !favourite }));
      }
      return !!favourite;
    },
    delete() {
      // folder may have changed, need to get store doc
      const storeDoc = get(this);
      if (storeDoc.docType === DocumentTypeEnum.Snippet) {
        // existing snippet deletion logic
        docStore.deleteSnippet(storeDoc.uuid);
      } else {
        // existing document deletion logic
        docStore.deleteDocument(storeDoc.uuid, storeDoc.folder.uuid);
      }
      docStore.api.deleteDocument(storeDoc.uuid, docStore.communitySlug);
    },
    moveAfterDragAndDrop(index: number, folder: Folder) {
      docStore.preventUpdates();
      autoSave = false;
      update((doc) => ({ ...doc, folder }));
      autoSave = true;
      docStore.api.moveDocument(docStore.communitySlug, doc.uuid, folder.uuid, index);
    },
    move(folder: Folder) {
      docStore.preventUpdates();
      autoSave = false;

      const previousParentUuid = get(this).folder.uuid;
      const previousParent = docStore.getFolder(previousParentUuid);
      previousParent?.removeItem(this.uuid);
      const newParent = docStore.getFolder(folder.uuid);
      const index = get(newParent).items.length;
      newParent?.addItem(this);

      update((doc) => ({ ...doc, folder }));
      autoSave = true;
      docStore.api.moveDocument(docStore.communitySlug, doc.uuid, folder.uuid, index);
    },
    updateFromJson,
    getNextDoc(): Document | undefined {
      const folders = get(docStore.folders);
      const flattenedDocuments = flattenSections(folders);
      const currentIndex = flattenedDocuments.findIndex((d) => d.uuid === this.uuid);

      if (currentIndex === -1) {
        reportError(new Error("Cannot get next document: current document not found"));
      } else if (currentIndex === flattenedDocuments.length - 1) {
        // No next doc, already at the last document
        return undefined;
      } else {
        return get(flattenedDocuments[currentIndex + 1]);
      }
    },
    getPreviousDoc(): Document | undefined {
      const folders = get(docStore.folders);
      const flattenedDocuments = flattenSections(folders);
      const currentIndex = flattenedDocuments.findIndex((d) => d.uuid === this.uuid);

      if (currentIndex === -1) {
        reportError(new Error("Cannot get next document: current document not found"));
      } else if (currentIndex === 0) {
        // No previous doc, already at the first document
        return undefined;
      } else {
        return get(flattenedDocuments[currentIndex - 1]);
      }
    },
  };
};

export enum DragType {
  Section = "section",
  NestedFolder = "nested_folder",
  NestedDocument = "nested_document",
}

export class DocumentsStore {
  // has the initial content loaded
  public isLoading: Writable<boolean>;
  public inited: boolean = false;
  // prevent updates from overwriting local changes
  private updatesAllowed: boolean = true;
  private updateTimeout: NodeJS.Timeout | undefined;

  // a store containing a map of Document stores for interacting with
  public documents: Writable<{ [key: string]: WritableDocument }>;
  // a store containing a map of plain Document objects for iterating through
  public _documents: Writable<{ [key: string]: Document }>;

  // a derived store of draft WritableDocuments
  public draftDocuments: Readable<WritableDocument[]>;

  // store of writable folder stores
  public folders: Writable<WritableFolder[]>;
  // derived stores on those folders
  public topLevelFolders: Writable<WritableFolder[]>;

  // store containing a flat list of categories
  // useful for read-only iterating in DOM
  public categoriesList: Readable<Folder[]>;

  // store containing a flat list of sections
  // useful for read-only iterating in DOM
  public sectionsList: Readable<Folder[]>;

  public currentDragType: Writable<DragType | undefined>;

  public communitySlug: string;
  public api;

  private updater: () => void = () => null;
  private unsubscribers: Array<Unsubscriber>;

  // a store containing a map of Snippet stores for interacting with
  public snippets: Writable<{ [key: string]: WritableDocument }> = writable({});
  // a store containing a map of plain snippets objects for iterating through
  public _snippets: Writable<{ [key: string]: Document }> = writable({});

  constructor(api: FolderTransportLayer) {
    this.documents = writable({});
    this._documents = writable({});
    this.draftDocuments = derived(this.documents, ($documents) =>
      Object.values($documents).filter((doc) => get(doc).isDraft)
    );
    this.folders = writable([]);
    this.topLevelFolders = writable([]);
    this.categoriesList = derived(this.folders, ($folders) =>
      $folders.map((f) => get(f)).filter((f) => f.categoryType === CategoryTypeEnum.Category)
    );
    this.sectionsList = derived(this.folders, ($folders) =>
      $folders.map((f) => get(f)).filter((f) => f.categoryType === CategoryTypeEnum.Section)
    );
    this.isLoading = writable(true);
    this.api = api;
    this.communitySlug = "";
    this.unsubscribers = [];
    this.currentDragType = writable(undefined);
    this.snippets = writable({});
    this._snippets = writable({});
  }

  destructor() {
    // unsubscribe all the subscribers
    this.unsubscribers.map((unsub) => unsub());
  }

  init(communitySlug: string) {
    this.inited = true;
    if (this.communitySlug) this.reset();

    this.communitySlug = communitySlug;
  }

  /** Periodically update docs from the server
   * Accepts a communitySlug param to make it easily reactive.
   */
  periodicallySync(communitySlug: string) {
    if (!browser) return;
    // clear previous updater
    if (this.updater) this.updater();
    // periodically update folders
    this.updater = this.api.onReceiveUpdate(communitySlug, (update) => {
      if (!this.updatesAllowed) {
        return;
      }
      this.update(update);
    });
  }

  reset() {
    this.documents.set({});
    this._documents.set({});
    this.folders.set([]);
    this.topLevelFolders.set([]);
    this.isLoading.set(true);
    this.communitySlug = "";
    this?.updater();
    this.snippets.set({});
    this._snippets.set({});
  }

  /**
   *  Prevents server updates for 5s.
   *  Debounced, so multiple calls will postpone it until last one lapses. */
  preventUpdates() {
    this.updatesAllowed = false;
    if (this.updateTimeout) clearTimeout(this.updateTimeout);
    // debounce updates
    this.updateTimeout = setTimeout(() => {
      this.updatesAllowed = true;
    }, 5000);
  }

  updateFolder(updatingFolder: WritableFolder) {
    this.folders.update((folders) => {
      return folders.map((f) => {
        if (f.uuid === updatingFolder.uuid) {
          return updatingFolder;
        }
        return f;
      });
    });
  }

  getUpdatedFolders(
    existingFolders: WritableFolder[],
    fetchedFolders: JSONFolder[],
    depth: number
  ) {
    const updated: Array<WritableFolder> = [];
    fetchedFolders.forEach((f) => {
      let folder = existingFolders.find((folder) => folder.uuid === f.uuid);
      if (folder) {
        folder.updateFromJson(f, depth);
      } else {
        folder = FolderStore(this, f, depth);
      }
      updated.push(folder, ...folder.getAllSubFolders());
    });

    return updated;
  }

  update(fetchedFolders: JSONFolder[], depth = 0) {
    let updated: Array<WritableFolder> = [];
    if (depth === 0) {
      this.folders.update((existingFolders) => {
        updated = this.getUpdatedFolders(existingFolders, fetchedFolders, depth);
        return updated;
      });
    } else {
      const existingFolders = get(this.folders);
      updated = this.getUpdatedFolders(existingFolders, fetchedFolders, depth);
    }

    if (depth === 0) {
      this.topLevelFolders.set(updated.slice(1).filter((f) => f.depth === 0));
    }
    return updated;
  }

  updateDocs(data: Partial<JSONDocument>[]) {
    const documents: WritableDocument[] = [];
    this.documents.update((docs) => {
      data.forEach((d) => {
        let doc = docs[d.uuid!];
        if (!doc) {
          // @ts-ignore
          doc = DocumentStore(this, d);
          docs[doc.uuid] = doc;
        } else {
          doc.updateFromJson(d);
        }
        documents.push(doc);
        const unsub = doc.subscribe((d) =>
          this._documents.update((docs) => ({ ...docs, [d.uuid]: d }))
        );
        this.unsubscribers.push(unsub);
      });
      return docs;
    });
    return documents;
  }
  /** Hydrates a document from a JSONDocument
   *
   * @returns WritableDocument
   *  */
  hydrateDoc(doc: JSONDocument) {
    return this.updateDocs([doc])[0];
  }
  async unfavourite(uuid: string) {
    const doc = get(this.documents)[uuid];
    if (doc) {
      doc.update((d) => {
        if (d.favourite !== undefined) {
          d.favourite = !d.favourite;
        }
        return d;
      });
    }
  }

  async fetch() {
    this.isLoading.set(true);
    this.update(await this.api.fetch(this.communitySlug));
    this.isLoading.set(false);
  }

  getDocumentSynchronously(uuid: string) {
    return get(this.documents)[uuid];
  }

  /** Creates a document store from json */
  jsonToDoc(json: JSONDocument) {
    return DocumentStore(this, json);
  }
  /** Stores the doc in the documents store */
  addToDocuments(doc: WritableDocument) {
    this.documents.update((docs) => ({ ...docs, [doc.uuid]: doc }));
  }
  async getDocument(uuid: string, communitySlug: string) {
    let doc = this.getDocumentSynchronously(uuid);

    if (doc) {
      const $doc = get(doc);
      if (!$doc.content) {
        const jsonDoc = await this.api.fetchDocument({
          fetch,
          communitySlug,
          uuid,
        });
        if (!jsonDoc) return null;
        doc.updateFromJson(jsonDoc);
      }
      return doc;
    }

    const jsonDoc = await this.api.fetchDocument({
      fetch,
      communitySlug,
      uuid,
    });
    if (!jsonDoc) return null;
    doc = DocumentStore(this, jsonDoc);
    this.documents.update((docs) => ({ ...docs, [doc.uuid]: doc }));
    return doc;
  }

  getFolder(uuid: string) {
    const folders = get(this.folders);
    const filtered = folders.filter((f) => f.uuid === uuid);
    // Sanity check to prevent insidious bugs caused by use of find
    if (filtered.length > 1) {
      throw Error("Folder not found");
    }
    return filtered[0];
  }

  async createDocument(isPublished?: boolean) {
    const doc = DocumentStore(this, {
      ...(await this.api.createDocument(this.communitySlug)),
      // user created it so fake perms as initial create is missing them
      myPermissions: {
        userId: "",
        permissions: ["delete_document", "add_document", "view_document", "change_document"],
      },
      isPublished: !!isPublished,
      isDraft: !isPublished,
    });
    this.documents.update((docs) => ({ ...docs, [doc.uuid]: doc }));
    return doc;
  }

  async moveDocument(docUUID: string, fromFolder: string, toFolder: string) {
    // remove from old folder
    if (fromFolder) {
      const from = this.getFolder(fromFolder);
      from?.removeItem(docUUID);
    }
    // add to new folder
    if (toFolder) {
      const to = this.getFolder(toFolder);
      const doc = await this.getDocument(docUUID, this.communitySlug);
      if (!doc) return;
      to?.addItem(doc);
    }
  }
  deleteDocument(doc: string, folder: string) {
    this.documents.update((docs) => {
      delete docs[doc];
      return docs;
    });
    this._documents.update((docs) => {
      delete docs[doc];
      return docs;
    });

    const parentFolder = this.getFolder(folder);
    if (parentFolder) {
      parentFolder.removeItem(doc);
    } else {
      console.log("no parentFolder");
    }
  }

  async createFolder(
    name: string,
    categoryType: CategoryTypeEnum,
    icon?: string,
    iconDark?: string
  ) {
    try {
      const folder = FolderStore(
        this,
        await this.api.createFolder(this.communitySlug, categoryType, name, icon, iconDark),
        0
      );
      this.folders.update((folders) => [...folders, folder]);
      this.topLevelFolders.update((folders) => [...folders, folder]);
    } catch (error) {
      if (error instanceof CanceledError) {
        alerts.error("Failed to create category/section.");
      } else {
        throw error;
      }
    }
  }

  deleteFolder(folder: Folder) {
    this.preventUpdates();

    const defaultFolder = get(this.folders)[0];
    const folderStore = this.getFolder(folder.uuid);
    // update direct children documents
    get(folderStore.docs).forEach((doc) => {
      doc.update((doc) => ({ ...doc, folder: get(defaultFolder) }));
      this.moveDocument(doc.uuid, folder.uuid, defaultFolder.uuid).then(() => {
        doc.setStatus(StatusD10Enum.Draft);
      });
    });
    // update parent section
    if (folder.parentUuid) {
      this.getFolder(folder.parentUuid).removeItem(folder.uuid);
    }
    // update parentUuid of children
    const subFolders = get(folderStore.folders);
    subFolders.forEach((f) => {
      f.update((f) => ({ ...f, parentUuid: undefined, depth: 0 }));
    });
    this.folders.update((folders) => folders.filter((f) => f.uuid !== folder.uuid));
    this.topLevelFolders.update((folders) => [
      ...folders.filter((f) => f.uuid !== folder.uuid),
      ...subFolders,
    ]);
  }

  deleteSnippet(uuid: string) {
    this.snippets.update((snips) => {
      delete snips[uuid];
      return snips;
    });
    this._snippets.update((snips) => {
      delete snips[uuid];
      return snips;
    });
  }

  updateSnippets(data: Partial<JSONDocument>[], prepend: boolean = false) {
    const snippets: WritableDocument[] = [];
    this.snippets.update((snips) => {
      data.forEach((s) => {
        if (!s?.uuid) return;
        let snippet = snips[s.uuid];
        if (!snippet) {
          snippet = DocumentStore(this, {
            ...s,
            docType: DocumentTypeEnum.Snippet,
          } as JSONDocument);
        } else {
          snippet.updateFromJson({ ...s, docType: DocumentTypeEnum.Snippet });
        }
        snippets.push(snippet);
        const unsub = snippet.subscribe((s) =>
          this._snippets.update((currentSnips) => {
            if (prepend) {
              return { [s.uuid]: s, ...currentSnips };
            }
            return { ...currentSnips, [s.uuid]: s };
          })
        );
        this.unsubscribers.push(unsub);
      });
      return prepend
        ? { ...Object.fromEntries(snippets.map((s) => [s.uuid, s])), ...snips }
        : snips;
    });
    return snippets;
  }

  hydrateSnippet(snippet: Partial<JSONDocument>) {
    return this.updateSnippets([snippet])[0];
  }

  async createSnippet(
    isPublished?: boolean,
    snippetData?: { title: string; content: { text: string; json: any; crdt: string } }
  ) {
    const snippet = DocumentStore(this, {
      ...(await this.api.createDocument(this.communitySlug, true, isPublished, snippetData)),
      docType: DocumentTypeEnum.Snippet,
      myPermissions: {
        userId: "",
        permissions: ["delete_document", "add_document", "view_document", "change_document"],
      },
    });
    // add new snippet to the beginning of the list to maintain newest-first order
    this.snippets.update((snippets) => ({
      [snippet.uuid]: snippet,
      ...snippets,
    }));
    this._snippets.update((snippets) => ({
      [snippet.uuid]: get(snippet),
      ...snippets,
    }));
    return snippet;
  }
}

export const isDraggingAtTheRoot = writable(false);
export const isDragging = writable(false);
